// ─────────────────────────────────────────────────────────────────────────────
// Selector de campaña de Brillamos Juntos, al PRINCIPIO de la barra de filtros (BJ-D3).
//
// Reglas (docs/contexto/REPORTERIA-REGLAS.md §8, War Room 2026-09-21):
//   · BJ-F4 — estatus Activa / Finalizada, uno u otro, nunca mezclados.
//   · BJ-F3 — la lista llega YA filtrada en cascada (estado + OUs de arriba); este componente solo
//     la pinta. La cascada vive en `campanasReporte.ts` para poder probarla sin React.
//   · BJ-D4 — cada opción dice alcance, vigencia y estado, como el puesto en el buscador de personas.
//     Un <select> nativo no admite dos renglones: va en la misma línea, separado por «·».
//   · BJ-D5 — ícono de ayuda que explica qué filtros se combinan con la campaña.
//   · 2026-10-22 (pase de contexto QA) — si el listado de campañas FALLA (403 sin permiso, o error),
//     el combo lo dice. Antes mostraba «No hay campañas con este estatus…», que se leía como que no
//     existían y ocultó que el backend rechazaba la consulta.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { getCampaignName } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import type { OUItem } from '@/api/types';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import {
  ESTADOS_CAMPANA_REPORTE, TODAS_LAS_CAMPANAS, alcanceNivelMasAlto, campanasDeTodas,
} from './campanasReporte';
import type { EstadoCampanaReporte } from './campanasReporte';

export interface SelectorCampanaReporteProps {
  readonly estado: EstadoCampanaReporte;
  readonly onEstado: (estado: EstadoCampanaReporte) => void;
  /** Campañas YA filtradas por estado y por las OUs de arriba (BJ-F3). */
  readonly campanas: readonly CampaignDto[];
  readonly campaignId: string;
  readonly onCampana: (campaignId: string) => void;
  /** Catálogo de OUs con padres: para truncar el alcance al nivel más alto (BJ-H4). */
  readonly ouItems: readonly OUItem[];
  readonly cargando?: boolean;
  /** El listado falló: `sinPermiso` (403) o `error` (cualquier otro). `null` = cargó bien. */
  readonly errorDeCarga?: ErrorDeCargaCampanas;
}

export type ErrorDeCargaCampanas = 'sinPermiso' | 'error' | null;

function fecha(iso: string | null, idioma: string): string {
  return iso ? new Date(iso).toLocaleDateString(idioma, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
}

export function SelectorCampanaReporte({
  estado, onEstado, campanas, campaignId, onCampana, ouItems, cargando = false, errorDeCarga = null,
}: SelectorCampanaReporteProps) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { resolveOuName } = useOuNameResolver();

  const etiquetaDe = (c: CampaignDto): string => {
    const alcance = alcanceNivelMasAlto(c.alcance ?? [], ouItems);
    const textoAlcance = alcance.length === 0 ? t('reports.common.scopeAll') : alcance.map(resolveOuName).join(', ');
    const vigencia = `${fecha(c.fechaInicio, i18n.language)} – ${fecha(c.fechaFin, i18n.language)}`;
    const textoEstado = t(`reports.shine.campaignStatus.${c.estado === 'Activa' ? 'activa' : 'finalizada'}`);
    return `${getCampaignName(c, lang)} · ${textoAlcance} · ${vigencia} · ${textoEstado}`;
  };

  const textoVacio = (): string => {
    if (errorDeCarga === 'sinPermiso') return t('reports.shine.campaignsForbidden');
    if (errorDeCarga === 'error') return t('reports.shine.campaignsLoadError');
    if (campanas.length === 0 && !cargando) return t('reports.shine.noCampaigns');
    return `${t('reports.shine.campaignSelector')}…`;
  };

  return (
    <div className="rr-tira-campana">
      <label className="rr-tira-campo">
        <span className="rr-sr-only">{t('reports.shine.campaignStatusLabel')}</span>
        <select
          className="rr-funnel-select"
          value={estado}
          onChange={(e) => onEstado(e.target.value as EstadoCampanaReporte)}
          aria-label={t('reports.shine.campaignStatusLabel')}
        >
          {ESTADOS_CAMPANA_REPORTE.map((e) => (
            <option key={e} value={e}>
              {t(`reports.shine.campaignStatus.${e === 'Activa' ? 'activa' : 'finalizada'}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="rr-tira-campo rr-tira-campo--campana">
        <span className="rr-sr-only">{t('reports.shine.campaignSelector')}</span>
        <select
          className="rr-funnel-select"
          value={campaignId}
          onChange={(e) => onCampana(e.target.value)}
          disabled={cargando}
          aria-label={t('reports.shine.campaignSelector')}
        >
          <option value="">
            {textoVacio()}
          </option>
          {/* BJT-1 (2026-10-14) — consolida las campañas Brillamos de la lista; con una sola no aporta nada. */}
          {campanasDeTodas(campanas).length > 1 && (
            <option value={TODAS_LAS_CAMPANAS}>{t('reports.shine.allCampaigns')}</option>
          )}
          {campanas.map((c) => <option key={c.id} value={c.id}>{etiquetaDe(c)}</option>)}
        </select>
      </label>

      <FloatingTooltip tooltipClassName="adm-vista-tooltip" content={t('reports.shine.filtersHelp')}>
        {(anchorProps) => (
          <button
            type="button"
            className="rr-tira-ayuda"
            aria-label={t('reports.shine.filtersHelpAria')}
            {...anchorProps}
          >
            <i className="fas fa-circle-question" aria-hidden="true" />
          </button>
        )}
      </FloatingTooltip>
    </div>
  );
}
